//@ts-nocheck

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MediaItem, MediaFile } from '../types';

interface PostVideoTrimmerProps {
  item: MediaItem;
  onSave: (startAt: number, endAt: number, capturedFrames?: MediaFile[]) => void;
  onClose: () => void;
  inline?: boolean; // When true, hides Save/Cancel buttons and reports trim changes live via onTrimChange
  onTrimChange?: (startAt: number, endAt: number) => void; // Called live as handles are dragged (inline mode)
}

interface Tick {
  position: number;
  major: boolean;
  label: string;
  value: number;
}

const PostVideoTrimmer: React.FC<PostVideoTrimmerProps> = ({ item, onSave, onClose, inline, onTrimChange }) => {
  const [videoDuration, setVideoDuration] = useState(0);
  const [startTime, setStartTime] = useState(item?.startAt || 0);
  const [endTime, setEndTime] = useState(item?.endAt || 10);
  const [currentPlayTime, setCurrentPlayTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState<'start' | 'end' | null>(null);
  const [ticks, setTicks] = useState<Tick[]>([]);
  const [loading, setLoading] = useState(false);
  const [capturedFrames, setCapturedFrames] = useState<MediaFile[]>([]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);

  const trimDuration = endTime - startTime;

  // Format time for display (mm:ss format)
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format time for tick labels
  const formatTimeForLabel = (seconds: number): string => {
    if (isNaN(seconds)) return '0';
    if (seconds >= 60) return formatTime(seconds);
    return Math.floor(seconds).toString();
  };

  // Determine the appropriate interval for major ticks
  const getMajorTickInterval = (duration: number): number => {
    if (duration <= 10) return 1;
    if (duration <= 60) return 5;
    if (duration <= 300) return 10;
    return 30;
  };

  // Generate tick marks for the ruler
  const generateTicks = useCallback((duration: number): void => {
    const newTicks: Tick[] = [];
    const majorInterval = getMajorTickInterval(duration);
    const minorTicksPerMajor = 10;

    for (let i = 0; i <= duration; i += majorInterval) {
      if (i > duration) break;
      const position = (i / duration) * 100;
      newTicks.push({
        position,
        major: true,
        label: formatTimeForLabel(i),
        value: i
      });

      if (i + majorInterval <= duration) {
        const minorInterval = majorInterval / minorTicksPerMajor;
        for (let j = 1; j < minorTicksPerMajor; j++) {
          const minorTime = i + (j * minorInterval);
          if (minorTime >= duration) break;
          const minorPosition = (minorTime / duration) * 100;
          newTicks.push({
            position: minorPosition,
            major: false,
            label: '',
            value: minorTime
          });
        }
      }
    }
    setTicks(newTicks);
  }, []);

  // Load video metadata
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      const duration = video.duration;
      setVideoDuration(duration);
      // Use existing endAt or default to min of 10 seconds or duration
      if (!item?.endAt) {
        setEndTime(Math.min(10, duration));
      }
      generateTicks(duration);
    };

    const handleTimeUpdate = () => {
      setCurrentPlayTime(video.currentTime);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [generateTicks, item?.endAt]);

  // Handle mouse/touch movement during drag
  const handleMove = useCallback((clientX: number) => {
    if (!isDragging || !dragType || !rulerRef.current) return;

    const rulerRect = rulerRef.current.getBoundingClientRect();
    const rulerWidth = rulerRect.width;
    const offsetX = clientX - rulerRect.left;

    let newTime = (offsetX / rulerWidth) * videoDuration;
    newTime = Math.round(newTime * 10) / 10;
    newTime = Math.max(0, Math.min(videoDuration, newTime));

    if (dragType === 'start') {
      newTime = Math.min(newTime, endTime - 0.5);
      setStartTime(newTime);
    } else {
      newTime = Math.max(newTime, startTime + 0.5);
      setEndTime(newTime);
    }

    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  }, [isDragging, dragType, videoDuration, endTime, startTime]);

  // Mouse events
  const handleMouseMove = useCallback((event: MouseEvent) => {
    handleMove(event.clientX);
  }, [handleMove]);

  // Touch events
  const handleTouchMove = useCallback((event: TouchEvent) => {
    if (event.touches.length > 0) {
      handleMove(event.touches[0].clientX);
    }
  }, [handleMove]);

  // End drag — fire onTrimChange in inline mode so parent gets final values
  const handleEnd = useCallback(() => {
    setIsDragging(false);
    setDragType(null);
    if (inline && onTrimChange) {
      onTrimChange(startTime, endTime);
    }
  }, [inline, onTrimChange, startTime, endTime]);

  // Setup event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, handleMouseMove, handleTouchMove, handleEnd]);

  const startDrag = (event: React.MouseEvent | React.TouchEvent, type: 'start' | 'end') => {
    setIsDragging(true);
    setDragType(type);
    event.preventDefault();
  };

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      if (video.currentTime >= endTime || video.currentTime < startTime) {
        video.currentTime = startTime;
      }
      video.play();

      const checkTime = () => {
        if (video.currentTime >= endTime) {
          video.pause();
          video.removeEventListener('timeupdate', checkTime);
        }
      };

      video.addEventListener('timeupdate', checkTime);
    }
  };

  const getVideoUrl = (): string => {
    if (item.media && item.media.length > 0) {
      const videoMedia = item.media.find(media => media.type === 'video');
      return videoMedia ? videoMedia.downloadURL : item.url;
    }
    return item.url;
  };

  const captureFrame = async () => {
    const video = videoRef.current;
    if (!video) return;

    const videoMedia = item.media?.find(media => media.type === 'video');
    if (!videoMedia || !videoMedia.path) return;

    setLoading(true);
    try {
      const response = await fetch('https://media-transcoder-255551438908.us-central1.run.app/get-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filePath: videoMedia.path,
          timeInSeconds: video.currentTime,
          quality: 95
        })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      const result = await response.json();

      const newMediaItem: MediaFile = {
        downloadURL: result.imageURL,
        path: result.imageURL.split('/').pop(),
        type: 'image' as const,
        primary: false
      };

      setCapturedFrames([...capturedFrames, newMediaItem]);
      console.log('Frame captured successfully:', newMediaItem);
    } catch (error) {
      console.error('Error capturing frame:', error);
      alert('Failed to capture frame');
    } finally {
      setLoading(false);
    }
  };

  const removeCapturedFrame = (index: number) => {
    setCapturedFrames(capturedFrames.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    // Save trim timestamps and captured frames
    onSave(startTime, endTime, capturedFrames.length > 0 ? capturedFrames : undefined);
    onClose();
  };

  return (
    <div className="w-full h-full flex flex-col bg-black">
      {/* Video Player */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <video
          ref={videoRef}
          src={getVideoUrl()}
          className="w-full h-full object-contain cursor-pointer"
          onClick={togglePlayPause}
        />

      </div>

      {/* Trimmer Controls Overlay - Bottom */}
      <div className="bg-white rounded-t-3xl shadow-2xl p-4 max-h-[40vh] overflow-y-auto">
        {/* Drag Handle */}
        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>

        {/* Play/Pause and Time Display */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={togglePlayPause} className="hover:bg-gray-100 rounded-full p-2">
            {isPlaying ? (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <div className="text-sm font-medium">
            {formatTime(currentPlayTime)} / {formatTime(videoDuration)}
          </div>
          {!inline && (
            <div className="flex gap-2">
              {/* Capture button - only show if media is downloaded */}
              {item.mediaDownloaded && (
                <button
                  onClick={captureFrame}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm flex items-center gap-1 disabled:opacity-50"
                  title="Capture current frame as image"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                  Capture
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-600 hover:text-gray-800 px-4 py-2 rounded-md text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm"
              >
                Save
              </button>
            </div>
          )}
        </div>

        {/* Captured Frames Preview */}
        {capturedFrames.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-blue-900">
                📸 {capturedFrames.length} frame{capturedFrames.length > 1 ? 's' : ''} captured
              </div>
              <div className="text-xs text-blue-700">
                Post will become a sidecar
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {capturedFrames.map((frame, index) => (
                <div key={index} className="relative flex-shrink-0">
                  <img
                    src={frame.downloadURL}
                    alt={`Captured frame ${index + 1}`}
                    className="w-16 h-16 object-cover rounded border border-blue-300"
                  />
                  <button
                    onClick={() => removeCapturedFrame(index)}
                    className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-0.5 hover:bg-red-700"
                    title="Remove frame"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Time Info */}
        <div className="flex justify-between items-center mb-4 text-xs">
          <div className="text-center">
            <div className="text-gray-500">Start</div>
            <div className="font-medium">{formatTime(startTime)}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-500">Duration</div>
            <div className="font-medium">{formatTime(trimDuration)}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-500">End</div>
            <div className="font-medium">{formatTime(endTime)}</div>
          </div>
        </div>

        {/* Trimmer Ruler */}
        <div className="relative">
          <div ref={rulerRef} className="relative h-12 md:h-16 bg-gray-100 rounded-lg overflow-hidden touch-none">
            {/* Ticks */}
            {ticks.map((tick, index) => (
              <div
                key={index}
                className={`absolute top-0 ${tick.major ? 'h-8 bg-gray-400' : 'h-4 bg-gray-300'} w-px`}
                style={{ left: `${tick.position}%` }}
              >
                {tick.major && (
                  <div className="absolute top-8 left-0 transform -translate-x-1/2 text-xs text-gray-600">
                    {tick.label}
                  </div>
                )}
              </div>
            ))}

            {/* Selection indicator */}
            <div
              className="absolute top-0 h-full bg-red-200 opacity-50"
              style={{
                left: `${(startTime / videoDuration) * 100}%`,
                width: `${(trimDuration / videoDuration) * 100}%`
              }}
            />

            {/* Playhead */}
            {!isDragging && videoDuration > 0 && (
              <div
                className="absolute top-0 h-full w-1 bg-blue-500 z-10 shadow-lg"
                style={{
                  left: `${Math.min(100, Math.max(0, (currentPlayTime / videoDuration) * 100))}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-blue-500 rounded-full"></div>
              </div>
            )}

            {/* Start handle */}
            <div
              className="absolute top-0 h-full w-12 bg-transparent cursor-ew-resize z-20 flex items-center justify-center touch-none group"
              style={{ left: `max(0.75rem, ${(startTime / videoDuration) * 100}% - 0.75rem)` }}
              onMouseDown={(e) => startDrag(e, 'start')}
              onTouchStart={(e) => startDrag(e, 'start')}
            >
              <div className="w-1.5 h-10 bg-red-600 rounded-full shadow-md group-hover:scale-125 transition-transform"></div>
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black/90 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none">
                {formatTime(startTime)}
              </div>
            </div>

            {/* End handle */}
            <div
              className="absolute top-0 h-full w-12 bg-transparent cursor-ew-resize z-20 flex items-center justify-center touch-none group"
              style={{ right: `max(0.75rem, 100% - ${(endTime / videoDuration) * 100}% - 0.75rem)` }}
              onMouseDown={(e) => startDrag(e, 'end')}
              onTouchStart={(e) => startDrag(e, 'end')}
            >
              <div className="w-1.5 h-10 bg-red-600 rounded-full shadow-md group-hover:scale-125 transition-transform"></div>
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black/90 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none">
                {formatTime(endTime)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostVideoTrimmer;
